/**
 * lua_tools.ts — PURE editor intelligence for the Lua IDE (no CodeMirror, no
 * DOM — runs under Node in verify:ide). Built on the lua_lint tokenizer so
 * strings/comments can never confuse any of it:
 *
 *   formatLua(src)            — Format Document: block-aware re-indentation
 *   outlineLua(src)           — the Outline / breadcrumb symbol list
 *   findOccurrences(src, id)  — every identifier occurrence (rename, highlight)
 *   findDefinition(src, id)   — go-to-definition (local/function/assignment)
 */
import { tokenizeLua, type LuaDiagnostic } from './lua_lint.js';

const INDENT = '  ';
const OPENERS = new Set(['function', 'if', 'for', 'while', 'repeat']);
const LINE_DEDENTERS = new Set(['end', 'until', 'else', 'elseif']);

interface LineInfo { indent: number; raw: boolean }

/**
 * Re-indent Lua by block depth (2 spaces). Content is NEVER changed — only the
 * leading whitespace of each line. Lines inside multi-line [[long strings]] are
 * left untouched, and a syntactically broken file (unbalanced blocks) formats
 * on a best-effort clamp rather than erroring.
 */
export function formatLua(src: string): string {
  const diags: LuaDiagnostic[] = [];
  const toks = tokenizeLua(src, diags);
  const lines = src.split('\n');

  // Lines that CONTINUE a multi-line string token are raw — never re-indented.
  const rawLines = new Set<number>();
  for (const t of toks) {
    if (t.kind !== 'str' || !t.text.includes('\n')) continue;
    const span = t.text.split('\n').length - 1;
    for (let l = t.line + 1; l <= t.line + span; l++) rawLines.add(l);
  }

  // Group tokens by line (tokens are already in order).
  const byLine = new Map<number, typeof toks>();
  for (const t of toks) {
    let arr = byLine.get(t.line);
    if (!arr) { arr = []; byLine.set(t.line, arr); }
    arr.push(t);
  }

  const info: LineInfo[] = [];
  let depth = 0;
  // for/while headers absorb their `do` (the lint's closedHeader trick).
  const blockStack: Array<{ kw: string; closedHeader: boolean }> = [];

  for (let ln = 1; ln <= lines.length; ln++) {
    if (rawLines.has(ln)) { info.push({ indent: 0, raw: true }); continue; }
    const lineToks = byLine.get(ln) ?? [];
    const first = lineToks[0];
    let indent = depth;
    if (first && ((first.kind === 'kw' && LINE_DEDENTERS.has(first.text)) ||
      (first.kind === 'op' && (first.text === '}' || first.text === ')' || first.text === ']')))) {
      indent = Math.max(0, depth - 1);
    }
    info.push({ indent, raw: false });

    for (const t of lineToks) {
      if (t.kind === 'kw') {
        if (OPENERS.has(t.text)) { blockStack.push({ kw: t.text, closedHeader: false }); depth++; }
        else if (t.text === 'do') {
          const top = blockStack[blockStack.length - 1];
          if (top && (top.kw === 'for' || top.kw === 'while') && !top.closedHeader) top.closedHeader = true;
          else { blockStack.push({ kw: 'do', closedHeader: true }); depth++; }
        } else if (t.text === 'end' || t.text === 'until') {
          blockStack.pop();
          depth = Math.max(0, depth - 1);
        }
      } else if (t.kind === 'op') {
        if (t.text === '{' || t.text === '(' || t.text === '[') depth++;
        else if (t.text === '}' || t.text === ')' || t.text === ']') depth = Math.max(0, depth - 1);
      }
    }
  }

  return lines
    .map((line, i) => {
      const li = info[i];
      if (!li || li.raw) return line;
      const body = line.trim();
      if (body.length === 0) return '';
      return INDENT.repeat(li.indent) + body;
    })
    .join('\n');
}

// ─── Outline ──────────────────────────────────────────────────────────────────

export interface OutlineEntry {
  name: string;
  line: number; // 1-based
  kind: 'function' | 'hook' | 'local';
}

/** Symbols for the Outline panel: functions, hook-table entries, top locals. */
export function outlineLua(src: string): OutlineEntry[] {
  const toks = tokenizeLua(src);
  const out: OutlineEntry[] = [];
  for (let k = 0; k < toks.length; k++) {
    const t = toks[k];
    // function name(...) / local function name(...)
    if (t.kind === 'kw' && t.text === 'function') {
      const nx = toks[k + 1];
      if (nx && nx.kind === 'name') out.push({ name: nx.text, line: t.line, kind: 'function' });
      continue;
    }
    // name = function  (hook-table entries + assigned lambdas)
    if (t.kind === 'name' && toks[k + 1]?.text === '=' && toks[k + 2]?.kind === 'kw' && toks[k + 2].text === 'function') {
      out.push({ name: t.text, line: t.line, kind: t.text.startsWith('on_') ? 'hook' : 'function' });
      continue;
    }
    // top-level local x = … (`local function` never lands here — the next token
    // is the `function` keyword, not a name; `local x = function` is rule 2's)
    if (t.kind === 'kw' && t.text === 'local' && t.col === 1) {
      const nx = toks[k + 1];
      if (!nx || nx.kind !== 'name') continue;
      if (toks[k + 2]?.text === '=' && toks[k + 3]?.kind === 'kw' && toks[k + 3].text === 'function') continue;
      out.push({ name: nx.text, line: t.line, kind: 'local' });
    }
  }
  return out;
}

// ─── Occurrences + definition ─────────────────────────────────────────────────

export interface Occurrence {
  line: number; // 1-based
  col: number;  // 1-based
  from: number; // absolute offset
  to: number;
}

function lineStarts(src: string): number[] {
  const starts = [0];
  for (let i = 0; i < src.length; i++) if (src[i] === '\n') starts.push(i + 1);
  return starts;
}

/**
 * Every occurrence of a PLAIN identifier (never inside strings/comments, never
 * a partial match — `count` doesn't match `counter`). Dotted names match their
 * exact dotted form only.
 */
export function findOccurrences(src: string, name: string): Occurrence[] {
  if (!name) return [];
  const toks = tokenizeLua(src);
  const starts = lineStarts(src);
  const out: Occurrence[] = [];
  for (const t of toks) {
    if (t.kind !== 'name' || t.text !== name) continue;
    const from = (starts[t.line - 1] ?? 0) + (t.col - 1);
    out.push({ line: t.line, col: t.col, from, to: from + name.length });
  }
  return out;
}

/**
 * The identifier's DEFINITION site: the first occurrence introduced by `local`,
 * `function`, or a top-level assignment (`name =`). Null when the name is only
 * ever read (or is an API builtin).
 */
export function findDefinition(src: string, name: string): Occurrence | null {
  if (!name) return null;
  const toks = tokenizeLua(src);
  const starts = lineStarts(src);
  for (let k = 0; k < toks.length; k++) {
    const t = toks[k];
    if (t.kind !== 'name' || t.text !== name) continue;
    const prev = toks[k - 1];
    const next = toks[k + 1];
    const declared =
      (prev?.kind === 'kw' && (prev.text === 'local' || prev.text === 'function')) ||
      (next?.kind === 'op' && next.text === '=');
    if (declared) {
      const from = (starts[t.line - 1] ?? 0) + (t.col - 1);
      return { line: t.line, col: t.col, from, to: from + name.length };
    }
  }
  return null;
}
